import { Calendar, Minus, Plus } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { IdentityFieldSummary } from '@/types/components/audiences/import-session'
import {
  FIELD_ICONS,
  formatFieldStateLabel,
  getStateBadgeVariant,
  identityFieldLabel,
} from '../lib/required-fields-ui'

interface RequiredFieldsGridProps {
  fields: IdentityFieldSummary[]
  /** Which field is expanded (shows its configuration). */
  activeFieldId?: string | null
  disabled?: boolean
  /**
   * Called when the user clicks a field that is NOT currently expanded.
   * The dirty-guard runs before this fires.
   */
  onSelectField: (fieldId: string) => void
  /**
   * Called when the user clicks the field that IS currently expanded
   * (i.e. they want to collapse it). No dirty-guard; draft state is preserved
   * so re-expanding shows the in-progress edit.
   */
  onCollapseField: () => void
  /** Rendered inside the expanded row (workspace for the active field). */
  expandedContent?: React.ReactNode
}

/**
 * Accordion list: each field is a div that expands to show its configuration.
 * One row is expanded at a time; expandedContent is rendered inside that row.
 *
 * Collapse and switch-field are intentionally separated into two callbacks
 * so the parent can apply the dirty-guard only on switch, not on collapse.
 */
export function RequiredFieldsGrid({
  fields,
  activeFieldId = null,
  disabled = false,
  onSelectField,
  onCollapseField,
  expandedContent,
}: RequiredFieldsGridProps) {
  const { t } = useTranslation('app')
  return (
    <div className="flex flex-col">
      {fields.map((field) => {
        const Icon = FIELD_ICONS[field.canonicalId] ?? Calendar
        const isExpanded = activeFieldId === field.canonicalId
        return (
          <div
            key={field.canonicalId}
            className="flex flex-col border-b border-border"
          >
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                isExpanded ? onCollapseField() : onSelectField(field.canonicalId)
              }
              aria-expanded={isExpanded}
              className={cn(
                'flex w-full items-center gap-3 py-5 text-left transition-opacity',
                'hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1 pr-2">
                <div className="flex items-center gap-2">
                  <p className="text-xl font-normal leading-[1.5] text-foreground dark:text-white">
                    {identityFieldLabel(field.canonicalId, field.label, t)}
                  </p>
                  {/* Badge inline with title — mobile only */}
                  <Badge
                    variant={getStateBadgeVariant(field.state)}
                    className={cn(
                      'md:hidden shrink-0 rounded-[25px] px-3 py-[3px] text-xs font-medium',
                      'border-0 backdrop-blur-[4.55px]',
                      (field.state === 'ready_from_inference' ||
                        field.state === 'ready_from_source' ||
                        field.state === 'ready_from_constant' ||
                        field.state === 'ready_from_runtime')
                        ? 'bg-primary/10 text-primary'
                        : 'bg-destructive/15 text-destructive dark:bg-destructive/25 dark:text-destructive',
                    )}
                  >
                    {formatFieldStateLabel(field.state)}
                  </Badge>
                </div>
                <div
                  className={cn(
                    'grid transition-all duration-300 ease-in-out',
                    isExpanded
                      ? 'grid-rows-[1fr] opacity-100'
                      : 'grid-rows-[0fr] opacity-0',
                  )}
                >
                  <p className="overflow-hidden text-base leading-[1.6] text-muted-foreground">
                    {t('audienceCreate.defineFieldForAudience', {
                      field: identityFieldLabel(field.canonicalId, field.label, t).toLowerCase(),
                    })}
                  </p>
                </div>
              </div>
              {/* Badge at row end — desktop only */}
              <Badge
                variant={getStateBadgeVariant(field.state)}
                className={cn(
                  'hidden md:inline-flex shrink-0 rounded-[25px] px-3 py-[3px] text-xs font-medium',
                  'border-0 backdrop-blur-[4.55px]',
                  (field.state === 'ready_from_inference' ||
                    field.state === 'ready_from_source' ||
                    field.state === 'ready_from_constant' ||
                    field.state === 'ready_from_runtime')
                    ? 'bg-primary/10 text-primary'
                    : 'bg-destructive/15 text-destructive dark:bg-destructive/25 dark:text-destructive',
                )}
              >
                {formatFieldStateLabel(field.state)}
              </Badge>
              <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center text-foreground/40 dark:text-white/60">
                <div className="relative h-5 w-5">
                  <Plus
                    className={cn(
                      'absolute inset-0 h-5 w-5 transition-all duration-200',
                      isExpanded ? 'scale-75 opacity-0' : 'scale-100 opacity-100',
                    )}
                  />
                  <Minus
                    className={cn(
                      'absolute inset-0 h-5 w-5 transition-all duration-200',
                      isExpanded ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
                    )}
                  />
                </div>
              </div>
            </button>
            <div
              className={cn(
                'grid transition-all duration-300 ease-in-out',
                isExpanded && expandedContent
                  ? 'grid-rows-[1fr]'
                  : 'grid-rows-[0fr]',
              )}
            >
              <div className="overflow-hidden">
                <div className="pb-5 pl-0 pr-0 md:pl-[56px] md:pr-[54px]">
                  {isExpanded ? expandedContent : null}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
